import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BreadCrumbService } from '@/core/service/bread-crumb.service';
import { TranslateService } from '@ngx-translate/core';

interface Breadcrumb {
    label: string;
    url: string;
}

@Component({
    selector: 'app-breadcrumb',
    standalone: true,
    imports: [CommonModule, RouterModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <div class="flex flex-col">
            <label class="text-2xl font-bold">
                {{ breadCrumbService.title() }}
            </label>
            <div class="flex items-center text-base text-gray-500">
                @for (item of breadcrumbs(); track $index; let last = $last) {
                    <span class="cursor-pointer" [class.text-gray-500]="!last" [class.text-orange-600]="last" [class.font-small]="last" [routerLink]="item.url">
                        {{ item.label }}
                    </span>
                    @if (!last) {
                        <span class="mx-1.5 text-xl text-gray-600">›</span>
                    }
                }
            </div>
        </div>
    `
})
export class AppBreadcrumb {
    readonly breadcrumbs = signal<Breadcrumb[]>([]);
    breadCrumbService = inject(BreadCrumbService);
    private translate = inject(TranslateService);

    constructor(private router: Router) {
        // Wait for translations to load, THEN build on navigation
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                switchMap(() => this.translate.get('_')) // waits for translations to be ready
            )
            .subscribe(() => this.rebuild());

        // Rebuild on language change (translations are already loaded here)
        this.translate.onLangChange
            .pipe(
                switchMap(() => this.translate.get('_')) // ensures new lang is fully loaded
            )
            .subscribe(() => this.rebuild());
    }

    private rebuild() {
        const root = this.router.routerState.snapshot.root;
        const list: Breadcrumb[] = [];
        this.build(root, [], list);
        this.breadcrumbs.set(list);
    }

    private build(route: ActivatedRouteSnapshot, parent: string[], list: Breadcrumb[]) {
        const current = parent.concat(route.url.map((u) => u.path));
        const labelKey = route.data['breadcrumb'];
        const menuKey = route.data['key'];
        if (menuKey) {
            this.breadCrumbService._title.set(this.translate.instant(menuKey));
        }

        if (labelKey) {
            const url = '/' + current.join('/');
            const label = this.translate.instant(labelKey);
            const last = list[list.length - 1];
            if (!last || last.label !== label) {
                list.push({ label, url });
            }
        }

        if (route.firstChild) {
            this.build(route.firstChild, current, list);
        }
    }
}
